// SPDX-License-Identifier: AGPL-3.0-or-later

import {Slate} from '@app/features/app/components/dialogs/components/Slate';
import {
	SettingsTabContainer,
	SettingsTabContent,
	SettingsTabHeader,
} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {DESKTOP_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as AuthSessionCommands from '@app/features/auth/commands/AuthSessionCommands';
import {DeviceRevokeModal} from '@app/features/auth/components/modals/DeviceRevokeModal';
import type {AuthSession} from '@app/features/auth/models/AuthSession';
import AuthSessionState from '@app/features/auth/state/AuthSession';
import {TRY_AGAIN_DESCRIPTOR, UNKNOWN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/DevicesTab.module.css';
import {formatShortRelativeTime} from '@fluxer/date_utils/src/DateDuration';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	CheckIcon,
	CheckSquareIcon,
	DeviceMobileIcon,
	MonitorIcon,
	SquareIcon,
	WifiSlashIcon,
	XIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

const REVOKE_DEVICE_DESCRIPTOR = msg({
	message: 'Revoke device',
	comment: 'Short label in the devices tab. Keep it concise.',
});
const NETWORK_ERROR_DESCRIPTOR = msg({
	message: 'Network error',
	comment: 'Error message in the devices tab.',
});
const WE_RE_HAVING_TROUBLE_CONNECTING_TO_THE_SPACE_DESCRIPTOR = msg({
	message: "We're having trouble connecting to the space-time continuum. Check your connection and try again.",
	comment: 'Error message in the devices tab.',
});
const EXIT_SELECTION_MODE_DESCRIPTOR = msg({
	message: 'Exit selection mode',
	comment: 'Short label in the devices tab. Keep it concise.',
});
const ENTER_SELECTION_MODE_DESCRIPTOR = msg({
	message: 'Enter selection mode',
	comment: 'Short label in the devices tab. Keep it concise.',
});
const CLEAR_SELECTION_DESCRIPTOR = msg({
	message: 'Clear selection',
	comment: 'Button or menu action label in the devices tab. Keep it concise.',
});
const SELECT_ALL_DESCRIPTOR = msg({
	message: 'Select all',
	comment: 'Button or menu action label in the devices tab. Keep it concise.',
});
const UNKNOWN_DEVICE_DESCRIPTOR = msg({
	message: 'Unknown device',
	comment: 'Fallback device or operating-system label in the devices tab when session client data is unavailable.',
});
const SIGN_OUT_SELECTED_DEVICES_DESCRIPTOR = msg({
	message: 'Sign out {deviceCount, plural, one {# device} other {# devices}}',
	comment: 'Danger button in the devices tab. Signs out the currently selected devices.',
});
const MOBILE_DEVICE_REGEX = /iOS|Android|Windows Phone|BlackBerry|Mobile/i;
const StatusDot = observer(() => (
	<div aria-hidden={true} className={styles.statusDot} data-flx="user.devices-tab.status-dot.status-dot" />
));
const CustomCheckbox = observer(({checked}: {checked: boolean}) => (
	<div
		className={clsx(styles.checkbox, checked ? styles.checkboxChecked : styles.checkboxUnchecked)}
		data-flx="user.devices-tab.custom-checkbox.checkbox"
	>
		{checked && (
			<CheckIcon weight="regular" className={styles.checkIcon} data-flx="user.devices-tab.custom-checkbox.check-icon" />
		)}
	</div>
));

interface AuthSessionProps {
	authSession: AuthSession;
	isCurrent?: boolean;
	isSelected?: boolean;
	onSelect?: (id: string, index: number, shiftKey: boolean) => void;
	index?: number;
	selectionMode?: boolean;
}

const AuthSessionItem: React.FC<AuthSessionProps> = observer(
	({authSession, isCurrent = false, isSelected, onSelect, index, selectionMode}) => {
		const {i18n} = useLingui();
		const clientOs = authSession.clientOs ?? i18n._(UNKNOWN_DEVICE_DESCRIPTOR);
		const clientPlatform = authSession.clientPlatform ?? i18n._(UNKNOWN_DESCRIPTOR);
		const isMobile = MOBILE_DEVICE_REGEX.test(authSession.clientOs ?? '');
		const isInteractive = selectionMode && !isCurrent && onSelect;
		const handleClick = isInteractive
			? (e: React.MouseEvent) => onSelect(authSession.id, index!, e.shiftKey)
			: undefined;
		const handleKeyDown = isInteractive
			? (e: React.KeyboardEvent) => {
					if (isKeyboardActivationKey(e.key)) {
						e.preventDefault();
						onSelect(authSession.id, index!, e.shiftKey);
					}
				}
			: undefined;
		const interactiveProps = isInteractive
			? {
					onClick: handleClick,
					onKeyDown: handleKeyDown,
					role: 'checkbox' as const,
					'aria-checked': Boolean(isSelected),
					tabIndex: 0,
				}
			: {};
		const platformLabel = authSession.clientPlatform === DESKTOP_PRODUCT_NAME ? DESKTOP_PRODUCT_NAME : clientPlatform;
		const hasLocation = Boolean(authSession.clientLocation);
		const locationRowVisible = hasLocation || !isCurrent;
		return (
			<div
				className={clsx(styles.authSession, selectionMode && !isCurrent && styles.authSessionSelectable)}
				data-flx="user.devices-tab.auth-session-item.auth-session"
				{...interactiveProps}
			>
				<div className={styles.authSessionContent} data-flx="user.devices-tab.auth-session-item.auth-session-content">
					<div className={styles.iconContainer} data-flx="user.devices-tab.auth-session-item.icon-container">
						{isMobile ? (
							<DeviceMobileIcon className={styles.icon} data-flx="user.devices-tab.auth-session-item.icon" />
						) : (
							<MonitorIcon className={styles.icon} data-flx="user.devices-tab.auth-session-item.icon--2" />
						)}
					</div>
					<div className={styles.authSessionInfo} data-flx="user.devices-tab.auth-session-item.auth-session-info">
						<span className={styles.authSessionTitle} data-flx="user.devices-tab.auth-session-item.auth-session-title">
							{clientOs}
							<StatusDot data-flx="user.devices-tab.auth-session-item.status-dot" />
							{platformLabel}
						</span>
						{locationRowVisible && (
							<div
								className={styles.authSessionLocation}
								data-flx="user.devices-tab.auth-session-item.auth-session-location"
							>
								{hasLocation && (
									<span className={styles.locationText} data-flx="user.devices-tab.auth-session-item.location-text">
										{authSession.clientLocation}
									</span>
								)}
								{!isCurrent && hasLocation && (
									<span
										aria-hidden
										className={styles.locationSeparator}
										data-flx="user.devices-tab.auth-session-item.location-separator"
									/>
								)}
								{!isCurrent && (
									<span className={styles.lastUsed} data-flx="user.devices-tab.auth-session-item.last-used">
										{formatShortRelativeTime(authSession.approxLastUsedAt ?? new Date(0))}
									</span>
								)}
							</div>
						)}
					</div>
				</div>
				<div className={styles.authSessionActions} data-flx="user.devices-tab.auth-session-item.auth-session-actions">
					{!isCurrent && !selectionMode && (
						<Tooltip text={i18n._(REVOKE_DEVICE_DESCRIPTOR)} data-flx="user.devices-tab.auth-session-item.tooltip">
							<button
								type="button"
								onClick={() =>
									ModalCommands.push(
										modal(() => (
											<DeviceRevokeModal
												sessionIdHashes={[authSession.id]}
												data-flx="user.devices-tab.auth-session-item.device-revoke-modal"
											/>
										)),
									)
								}
								className={styles.revokeButton}
								data-flx="user.devices-tab.auth-session-item.revoke-button.push"
							>
								<XIcon
									className={styles.revokeIcon}
									weight="bold"
									data-flx="user.devices-tab.auth-session-item.revoke-icon"
								/>
							</button>
						</Tooltip>
					)}
					{selectionMode && !isCurrent && (
						<CustomCheckbox checked={!!isSelected} data-flx="user.devices-tab.auth-session-item.custom-checkbox" />
					)}
				</div>
			</div>
		);
	},
);
const DevicesTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const authSessionIdHash = AuthSessionState.authSessionIdHash;
	const authSessions = AuthSessionState.authSessions;
	const fetchStatus = AuthSessionState.fetchStatus;
	const otherDevices = authSessions.filter((authSession) => authSession.id !== authSessionIdHash);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedDevices, setSelectedDevices] = useState(new Set<string>());
	const [lastToggledIndex, setLastToggledIndex] = useState(-1);
	useEffect(() => {
		AuthSessionCommands.fetch();
	}, []);
	const toggleSelectionMode = (value: boolean) => {
		setSelectionMode(value);
		if (!value) {
			setSelectedDevices(new Set());
			setLastToggledIndex(-1);
		}
	};
	const toggleDevice = (deviceId: string, index: number, isShiftSelect: boolean) => {
		if (!selectionMode) {
			toggleSelectionMode(true);
		}
		setSelectedDevices((prev) => {
			const newSelection = new Set(prev);
			if (isShiftSelect && lastToggledIndex !== -1) {
				const start = Math.min(lastToggledIndex, index);
				const end = Math.max(lastToggledIndex, index);
				const shouldAdd = !prev.has(deviceId);
				otherDevices.slice(start, end + 1).forEach((device) => {
					if (shouldAdd) {
						newSelection.add(device.id);
					} else {
						newSelection.delete(device.id);
					}
				});
			} else {
				if (prev.has(deviceId)) {
					newSelection.delete(deviceId);
				} else {
					newSelection.add(deviceId);
				}
			}
			return newSelection;
		});
		setLastToggledIndex(index);
	};
	const handleSelectAll = () => {
		if (selectedDevices.size === otherDevices.length) {
			setSelectedDevices(new Set());
		} else {
			setSelectedDevices(new Set(otherDevices.map((device) => device.id)));
		}
	};
	if (fetchStatus === 'idle' || fetchStatus === 'pending') {
		return (
			<div className={styles.loadingContainer} data-flx="user.devices-tab.loading-container">
				<Spinner data-flx="user.devices-tab.spinner" />
			</div>
		);
	}
	const currentSession = authSessions.find((authSession) => authSession.id === authSessionIdHash);
	if (fetchStatus === 'error' || !currentSession) {
		return (
			<Slate
				icon={WifiSlashIcon}
				title={i18n._(NETWORK_ERROR_DESCRIPTOR)}
				description={i18n._(WE_RE_HAVING_TROUBLE_CONNECTING_TO_THE_SPACE_DESCRIPTOR)}
				buttonText={i18n._(TRY_AGAIN_DESCRIPTOR)}
				onClick={() => AuthSessionCommands.fetch()}
				data-flx="user.devices-tab.slate.fetch"
			/>
		);
	}
	return (
		<SettingsTabContainer data-flx="user.devices-tab.settings-tab-container">
			<SettingsTabHeader
				title={<Trans>My devices</Trans>}
				description={<Trans>Every device signed in to your account. Revoke any session you don't recognize.</Trans>}
				data-flx="user.devices-tab.settings-tab-header"
			/>
			<SettingsTabContent data-flx="user.devices-tab.settings-tab-content">
				<div className={styles.devicesSection} data-flx="user.devices-tab.devices-section">
					<div className={styles.deviceGroup} data-flx="user.devices-tab.device-group">
						<h3 className={styles.deviceGroupTitle} data-flx="user.devices-tab.device-group-title">
							<Trans>Current device</Trans>
						</h3>
						<AuthSessionItem
							authSession={currentSession}
							isCurrent={true}
							data-flx="user.devices-tab.auth-session-item"
						/>
					</div>
					{otherDevices.length > 0 && (
						<div className={styles.deviceGroup} data-flx="user.devices-tab.device-group--2">
							<div className={styles.deviceGroupHeader} data-flx="user.devices-tab.device-group-header">
								<h3 className={styles.deviceGroupTitle} data-flx="user.devices-tab.device-group-title--2">
									<Trans>Other devices</Trans>
								</h3>
								{otherDevices.length > 1 && (
									<div className={styles.actionsContainer} data-flx="user.devices-tab.actions-container">
										<Tooltip
											text={
												selectionMode ? i18n._(EXIT_SELECTION_MODE_DESCRIPTOR) : i18n._(ENTER_SELECTION_MODE_DESCRIPTOR)
											}
											data-flx="user.devices-tab.tooltip"
										>
											<button
												type="button"
												onClick={() => toggleSelectionMode(!selectionMode)}
												className={styles.actionButton}
												data-flx="user.devices-tab.action-button.toggle-selection-mode"
											>
												{selectionMode ? (
													<XIcon weight="bold" className={styles.actionIcon} data-flx="user.devices-tab.action-icon" />
												) : (
													<CheckSquareIcon className={styles.actionIcon} data-flx="user.devices-tab.action-icon--2" />
												)}
											</button>
										</Tooltip>
										{selectionMode && (
											<Tooltip
												text={
													selectedDevices.size === otherDevices.length
														? i18n._(CLEAR_SELECTION_DESCRIPTOR)
														: i18n._(SELECT_ALL_DESCRIPTOR)
												}
												data-flx="user.devices-tab.tooltip--2"
											>
												<button
													type="button"
													onClick={handleSelectAll}
													className={styles.actionButton}
													data-flx="user.devices-tab.action-button.select-all"
												>
													{selectedDevices.size === otherDevices.length ? (
														<SquareIcon className={styles.actionIcon} data-flx="user.devices-tab.action-icon--3" />
													) : (
														<CheckSquareIcon className={styles.actionIcon} data-flx="user.devices-tab.action-icon--4" />
													)}
												</button>
											</Tooltip>
										)}
									</div>
								)}
							</div>
							<div className={styles.devicesGrid} data-flx="user.devices-tab.devices-grid">
								{otherDevices.map((authSession, index) => (
									<AuthSessionItem
										key={authSession.id}
										authSession={authSession}
										isSelected={selectedDevices.has(authSession.id)}
										onSelect={toggleDevice}
										index={index}
										selectionMode={selectionMode}
										data-flx="user.devices-tab.auth-session-item.toggle-device"
									/>
								))}
							</div>
							{(otherDevices.length > 1 || (selectionMode && selectedDevices.size > 0)) && (
								<div className={styles.logoutSection} data-flx="user.devices-tab.logout-section">
									<Button
										variant="danger-secondary"
										onClick={() => {
											if (selectionMode && selectedDevices.size > 0) {
												ModalCommands.push(
													modal(() => (
														<DeviceRevokeModal
															sessionIdHashes={Array.from(selectedDevices)}
															data-flx="user.devices-tab.device-revoke-modal"
														/>
													)),
												);
											} else {
												ModalCommands.push(
													modal(() => (
														<DeviceRevokeModal
															sessionIdHashes={otherDevices.map((authSession) => authSession.id)}
															data-flx="user.devices-tab.device-revoke-modal--2"
														/>
													)),
												);
											}
										}}
										data-flx="user.devices-tab.button"
									>
										{selectionMode && selectedDevices.size > 0 ? (
											i18n._(SIGN_OUT_SELECTED_DEVICES_DESCRIPTOR, {deviceCount: selectedDevices.size})
										) : (
											<Trans>Sign out all other devices</Trans>
										)}
									</Button>
									<p className={styles.logoutDescription} data-flx="user.devices-tab.logout-description">
										<Trans>You'll have to sign back in on all signed-out devices</Trans>
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default DevicesTab;
